import { useEffect, useState } from 'react';

import { Calendar } from 'baseui/datepicker';
import { PillButton } from 'ui-kit/Buttons';
import { usePopout, FloatingPortal } from 'ui-kit/hooks/usePopout';

import { BaseFiltersConfig, DateRangeFilter, DateRangeFilterConfigEntry } from '../filters.types';
import {
  secondsToCalendarDate,
  startSeconds,
  endSeconds,
  formatSeconds,
} from '../dateRangeSeconds';

import type { CommonProps } from '../PillFilters';

export function DateRangePillFilter<T extends BaseFiltersConfig>({
  dateRangeFilter,
  filterConfig,
  removeFilter,
  updateFilter,
  index,
  className = '',
}: CommonProps<T> & {
  dateRangeFilter: DateRangeFilter<T>;
  filterConfig: DateRangeFilterConfigEntry;
  className?: string;
}) {
  const { refs, getFloatingProps, getReferenceProps, isOpen, floatingStyles } = usePopout({
    offset: 2,
    // Calendar's month/year <Select> portals its own dropdown menu to a
    // node outside this popout's floating panel (baseui's Popover/Menu,
    // marked with these data-baseweb values) — without this, picking a
    // month or year reads as an outside press and closes the whole
    // date-range picker, discarding the in-progress first-click selection.
    ignoreOutsidePressWithin: '[data-baseweb="popover"], [data-baseweb="menu"]',
  });

  const isUnset =
    dateRangeFilter.min === filterConfig.min && dateRangeFilter.max === filterConfig.max;

  const [selected, setSelected] = useState<Date[]>(
    isUnset
      ? []
      : [secondsToCalendarDate(dateRangeFilter.min), secondsToCalendarDate(dateRangeFilter.max)],
  );

  useEffect(() => {
    setSelected(
      isUnset
        ? []
        : [secondsToCalendarDate(dateRangeFilter.min), secondsToCalendarDate(dateRangeFilter.max)],
    );
  }, [dateRangeFilter.min, dateRangeFilter.max, isUnset, isOpen]);

  const value = isUnset
    ? 'Any'
    : `${formatSeconds(dateRangeFilter.min)} to ${formatSeconds(dateRangeFilter.max)}`;

  return (
    <>
      <PillButton
        {...getReferenceProps()}
        ref={refs.setReference}
        description={filterConfig.displayName}
        value={value}
        className={className}
        iconName="pill-close"
        iconOnClick={() => removeFilter(dateRangeFilter.name, index)}
      />

      {isOpen ? (
        <FloatingPortal>
          <div
            // border-gray-700/bg-gray-950/z-40 match every other flyout panel
            // in PillFilters (RangePillButton, PillButtonCombobox, PillDropdown)
            // — was border-brand-500/bg-black/z-10, the same panel-styling
            // mismatch fixed everywhere else.
            className="z-40 rounded-lg border border-solid border-gray-700 bg-gray-950 p-4"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <Calendar
              aria-label="Select a date range"
              range
              value={selected}
              minDate={secondsToCalendarDate(filterConfig.min)}
              maxDate={secondsToCalendarDate(filterConfig.max)}
              onChange={({ date }) => {
                const dates = (Array.isArray(date) ? date : [date]).filter(
                  (d): d is Date => d != null,
                );
                setSelected(dates);
                if (dates.length === 2) {
                  updateFilter(dateRangeFilter.name, index, [
                    startSeconds(dates[0]),
                    endSeconds(dates[1]),
                  ]);
                }
              }}
            />
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}
